/**
 * THE COMPOSER'S MENU AND ITS GUARDRAILS (feature 0059 — founder 2026-08-21: «on envoie à Claude le
 * produit et la totalité des sections du builder, et en tant qu'expert marketing il nous dit
 * lesquelles implémenter»).
 *
 * The MENU is the curated slice of the component library the composer may pick from: sections whose
 * content the run can fully generate (texts + images + real products). Deliberately absent:
 *  - integrity types (testimonials, results, comparison…) — never invented, so never composed in;
 *  - collection-driven sections — the edit canvas cannot resolve collections (they would hide);
 *  - media types needing seller uploads (video…), app-driven types, chrome.
 * Each entry carries the one-line MARKETING purpose the model reasons with — sales language, not
 * schema language.
 *
 * `sanitizeComposition` is the trust boundary: the model's answer is TEXT, and every id is validated
 * against the menu AND the section's own `templates` declaration before it may reach `composePage`.
 * Too short or unusable → null, and the caller falls back to SCRATCH_BLUEPRINT — the composer can
 * only ever improve a page, never break the run.
 */
import { sectionRegistry } from '../../canvas/sectionRegistry';
import { type TemplateKey } from '@aquilixshop/ui-react';

export interface MenuEntry {
  id: string;
  purpose: string;
}

export const COMPOSER_MENU: readonly MenuEntry[] = [
  {
    id: 'main-product',
    purpose:
      "La fiche d'achat : galerie, prix, variantes, bouton d'achat. Obligatoire en tête de page produit.",
  },
  {
    id: 'slideshow-hero',
    purpose: "Grande ouverture visuelle plein écran : promesse de la marque et appel à l'action.",
  },
  { id: 'hero', purpose: 'Ouverture visuelle simple : une image, une promesse, un bouton.' },
  {
    id: 'image-with-text',
    purpose: 'Image et texte côte à côte : raconter la marque, un bénéfice clé ou une histoire.',
  },
  {
    id: 'custom-columns',
    purpose: "Trois à quatre colonnes illustrées : les points forts ou bénéfices de l'offre.",
  },
  {
    id: 'icon-bar',
    purpose: "Rangée d'icônes de réassurance : livraison, retours, garantie, paiement.",
  },
  { id: 'trust-row', purpose: 'Garanties concrètes mises en avant pour lever les freins.' },
  {
    id: 'multirow',
    purpose:
      "Alternance image/texte sur plusieurs rangées : usages, rituels ou étapes d'utilisation.",
  },
  {
    id: 'timeline',
    purpose: 'Étapes numérotées : comment utiliser le produit, ou son parcours de fabrication.',
  },
  { id: 'rich-text', purpose: 'Paragraphe éditorial court pour poser le ton de la marque.' },
  { id: 'collage', purpose: "Mosaïque d'images avec légendes : l'univers visuel de la marque." },
  { id: 'content-tabs', purpose: 'Onglets de contenu : caractéristiques, entretien, composition.' },
  {
    id: 'collapsible-content',
    purpose: 'Accordéons de détails pratiques : dimensions, livraison, entretien.',
  },
  {
    id: 'faq',
    purpose: "Questions fréquentes d'acheteur avec réponses courtes — lève les objections.",
  },
  { id: 'related-products', purpose: "Suggestions d'autres produits du catalogue de la boutique." },
  {
    id: 'featured-product',
    purpose: "Met un produit du catalogue en vedette avec son bouton d'achat.",
  },
  { id: 'newsletter', purpose: "Capture d'e-mails avec une raison honnête de s'abonner." },
  {
    id: 'horizontal-ticker',
    purpose: 'Bandeau défilant de mentions courtes : énergie et rythme visuel.',
  },
];

/** Openers the index page must start with — prepended when the composer forgot one. */
const INDEX_OPENERS = ['slideshow-hero', 'hero', 'parallax-hero', 'slideshow'];

const MIN_SECTIONS = 3;
const MAX_SECTIONS = 9;

/**
 * Parse and guard one page's composer answer. Returns the safe ordered list, or null when the
 * answer is unusable (fallback to the fixed blueprint).
 */
export function sanitizeComposition(raw: string, template: TemplateKey): string[] | null {
  const menuIds = new Set(COMPOSER_MENU.map((m) => m.id));
  const picked: string[] = [];
  for (const part of raw.split(',')) {
    const id = part.trim();
    if (!id || !menuIds.has(id)) continue;
    if (!sectionRegistry[id]) continue;
    const templates = sectionRegistry[id]?.schema.templates as readonly string[] | undefined;
    if (templates && !templates.includes(template)) continue;
    // main-product only ever belongs to the product page, once.
    if (id === 'main-product' && (template !== 'product' || picked.includes(id))) continue;
    picked.push(id);
    if (picked.length >= MAX_SECTIONS) break;
  }
  if (template === 'product') {
    // The buy box OPENS the product page, wherever the composer placed it — a shopper who lands on
    // a product URL must see the product, its price and the buy button before the storytelling.
    const at = picked.indexOf('main-product');
    if (at > 0) picked.splice(at, 1);
    if (at !== 0) picked.unshift('main-product');
  }
  if (template === 'index' && !picked.some((id) => INDEX_OPENERS.includes(id))) {
    picked.unshift('slideshow-hero');
  }
  return picked.length >= MIN_SECTIONS ? picked : null;
}
